import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import { useHealthWatch } from '../../hooks/queries'
import { naechsterStartAusDemNetz } from '../../lib/huelle'
import { maintenanceOver, type Maintenance } from '../../lib/maintenance'
import { useTexte } from '../../texte'

/**
 * The maintenance page (#131, 1.114.0): what everyone sees whom the server
 * does not let through. It replaces the whole app (`App`) -- the outbox's
 * sender is gone with it, so nothing tries to go out in the meantime.
 *
 * **It ends itself.** It asks `/api/health` every half minute and when the
 * app comes back to the front; once the maintenance is off -- or this device
 * passes now (signed in as someone who may) -- it reloads once, from the
 * net, because after a maintenance the server mostly runs a new version
 * (`naechsterStartAusDemNetz()`, the same way as the `StandWache`).
 *
 * **"Anmelden" stays**, quietly: the supervisor or an author on a second
 * device has to get in; signing in is open on the server during the
 * maintenance, and the next answer decides.
 */
export function MaintenancePage({ maintenance }: { maintenance: Maintenance }) {
  const texte = useTexte()
  const t = texte.rahmen.maintenance
  const health = useHealthWatch()
  const over = health.data !== undefined && (health.data.maintenance === null || health.data.maintenance.you_pass)
  const once = useRef(false)

  useEffect(() => {
    if (!over || once.current) return
    once.current = true
    maintenanceOver()
    void naechsterStartAusDemNetz().then(() => window.location.reload())
  }, [over])

  // The newest the server said -- the 503 that brought us here may be older.
  const state = health.data?.maintenance ?? maintenance
  const until = state.until === null || state.until === undefined ? null : new Date(state.until)
  const longer = until !== null && until.getTime() < Date.now()

  return (
    <div className="centered">
      <div className="wartetafel" data-maintenance-page="">
        <div className="card" role="status">
          <h1>{t.title}</h1>
          <p className="card__lead" data-maintenance-text="">{state.text || t.fallback}</p>
          {until !== null && (
            <p className="card__lead" data-maintenance-until={state.until ?? ''}>
              {longer
                ? t.longer
                : t.backAt(
                    until.toLocaleString(texte.allgemein.gebiet, {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    }),
                  )}
            </p>
          )}
          <p className="leise">{t.kept}</p>
          <button
            className="button button--outline"
            type="button"
            data-maintenance-check=""
            disabled={health.isFetching}
            onClick={() => void health.refetch()}
          >
            {t.check}
          </button>
        </div>
        <Link className="button button--quiet button--klein button--mittig" to="/login" data-maintenance-signin="">
          {t.signIn}
        </Link>
      </div>
    </div>
  )
}
